/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  Image,
  FlatList,
  Animated,
  Dimensions,
  Share,
  Platform,
  Switch,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
  Feather,
} from '@expo/vector-icons';

export default function PreviousCoursesScreen({ navigation }) {
  // States
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState('list'); // 'list', 'grid', 'timeline', 'category'
  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    dateRange: [],
    category: [],
    completion: [],
    certification: false,
  });
  const [sortBy, setSortBy] = useState('date-desc'); // 'date-desc', 'date-asc', 'title-asc', 'title-desc'

  // Animation values
  const filterHeight = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(1)).current;

  // Mock course data
  const mockCourses = [
    {
      id: '1',
      title: 'Advanced Mechanical Engineering Principles',
      provider: 'Imperial College London',
      category: 'Mechanical Engineering',
      completionDate: '2025-03-15',
      duration: '12 weeks',
      score: 92,
      certificate: true,
      certificateUrl: 'https://certs.jobeng.com/mech-eng-123',
      progress: 100,
      thumbnail: 'https://images.unsplash.com/photo-1537462715879-360eeb61a0ad',
      skills: [
        'Thermodynamics',
        'Fluid Mechanics',
        'Structural Analysis',
        'CAD',
      ],
      description:
        'Advanced course covering the fundamental principles of mechanical engineering with practical applications in modern industries.',
    },
    {
      id: '2',
      title: 'Electrical Systems Design',
      provider: 'University of Manchester',
      category: 'Electrical Engineering',
      completionDate: '2025-01-20',
      duration: '8 weeks',
      score: 88,
      certificate: true,
      certificateUrl: 'https://certs.jobeng.com/elec-sys-456',
      progress: 100,
      thumbnail: 'https://images.unsplash.com/photo-1555664424-778a1e5e1b48',
      skills: [
        'Circuit Design',
        'Power Systems',
        'Electrical Safety',
        'AutoCAD Electrical',
      ],
      description:
        'Comprehensive course on designing electrical systems for commercial and industrial applications.',
    },
    {
      id: '3',
      title: 'Introduction to Civil Engineering',
      provider: 'University of Edinburgh',
      category: 'Civil Engineering',
      completionDate: '2024-11-05',
      duration: '10 weeks',
      score: 78,
      certificate: true,
      certificateUrl: 'https://certs.jobeng.com/civil-789',
      progress: 100,
      thumbnail: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e',
      skills: [
        'Structural Engineering',
        'Construction Management',
        'Surveying',
        'Materials Science',
      ],
      description:
        'Foundational course covering the principles and practices of civil engineering and infrastructure development.',
    },
    {
      id: '4',
      title: 'Software Engineering for Embedded Systems',
      provider: 'University of Cambridge',
      category: 'Software Engineering',
      completionDate: null,
      duration: '14 weeks',
      score: null,
      certificate: false,
      certificateUrl: null,
      progress: 65,
      thumbnail: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97',
      skills: ['C/C++', 'Microcontrollers', 'RTOS', 'Embedded Systems Design'],
      description:
        'Specialized course on developing software for embedded systems and IoT devices.',
    },
    {
      id: '5',
      title: 'Aerospace Engineering Fundamentals',
      provider: 'University of Bristol',
      category: 'Aerospace Engineering',
      completionDate: '2024-09-12',
      duration: '16 weeks',
      score: 94,
      certificate: true,
      certificateUrl: 'https://certs.jobeng.com/aero-101',
      progress: 100,
      thumbnail: 'https://images.unsplash.com/photo-1557800636-894a64c1696f',
      skills: [
        'Aerodynamics',
        'Aircraft Structures',
        'Propulsion Systems',
        'Flight Mechanics',
      ],
      description:
        'Comprehensive introduction to aerospace engineering principles and aircraft design.',
    },
    {
      id: '6',
      title: 'Renewable Energy Systems',
      provider: 'University of Strathclyde',
      category: 'Energy Engineering',
      completionDate: '2024-08-25',
      duration: '9 weeks',
      score: 85,
      certificate: true,
      certificateUrl: 'https://certs.jobeng.com/renewable-202',
      progress: 100,
      thumbnail: 'https://images.unsplash.com/photo-1509391366360-2e959784a276',
      skills: [
        'Solar Energy',
        'Wind Power',
        'Energy Storage',
        'Grid Integration',
      ],
      description:
        'Specialized course on designing and implementing renewable energy systems for sustainable power generation.',
    },
    {
      id: '7',
      title: 'Chemical Process Engineering',
      provider: 'University College London',
      category: 'Chemical Engineering',
      completionDate: null,
      duration: '12 weeks',
      score: null,
      certificate: false,
      certificateUrl: null,
      progress: 30,
      thumbnail: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69',
      skills: [
        'Process Design',
        'Reaction Engineering',
        'Separation Processes',
        'Plant Safety',
      ],
      description:
        'Advanced course on chemical process engineering for industrial applications.',
    },
    {
      id: '8',
      title: 'Robotics and Automation',
      provider: 'University of Sheffield',
      category: 'Robotics',
      completionDate: '2024-06-10',
      duration: '11 weeks',
      score: 90,
      certificate: true,
      certificateUrl: 'https://certs.jobeng.com/robotics-303',
      progress: 100,
      thumbnail: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a',
      skills: [
        'Robot Kinematics',
        'Sensor Integration',
        'Control Systems',
        'Computer Vision',
      ],
      description:
        'Comprehensive course on robotics principles, design, and industrial automation applications.',
    },
  ];

  // Toggle filter visibility
  const toggleFilter = () => {
    setFilterVisible(!filterVisible);
    Animated.timing(filterHeight, {
      toValue: filterVisible ? 0 : 1,
      duration: 300,
      useNativeDriver: false,
    }).start();
  };

  // Handle filter selection
  const handleFilterSelect = (category, value) => {
    const currentFilters = { ...selectedFilters };

    if (category === 'certification') {
      currentFilters[category] = !currentFilters[category];
    } else {
      if (currentFilters[category].includes(value)) {
        currentFilters[category] = currentFilters[category].filter(
          item => item !== value
        );
      } else {
        currentFilters[category] = [...currentFilters[category], value];
      }
    }

    setSelectedFilters(currentFilters);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedFilters({
      dateRange: [],
      category: [],
      completion: [],
      certification: false,
    });
  };

  // Share certificate
  const shareCertificate = course => {
    Share.share({
      message: `I've completed the "${course.title}" course from ${course.provider} with a score of ${course.score}%. View my certificate: ${course.certificateUrl}`,
      title: `${course.title} Certificate`,
    });
  };

  // Filter courses based on selected filters and search query
  const getFilteredCourses = () => {
    let filtered = [...mockCourses];

    // Filter by search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        course =>
          String(course.title ?? '').toLowerCase().includes(query) ||
          String(course.provider ?? '').toLowerCase().includes(query) ||
          String(course.category ?? '').toLowerCase().includes(query) ||
          course.skills.some(skill => skill.toLowerCase().includes(query)) ||
          String(course.description ?? '').toLowerCase().includes(query)
      );
    }

    // Filter by category
    if (selectedFilters.category.length > 0) {
      filtered = filtered.filter(course =>
        selectedFilters.category.includes(course.category)
      );
    }

    // Filter by completion status
    if (selectedFilters.completion.length > 0) {
      filtered = filtered.filter(course => {
        if (
          selectedFilters.completion.includes('Completed') &&
          course.progress === 100
        )
          return true;
        if (
          selectedFilters.completion.includes('In Progress') &&
          course.progress < 100
        )
          return true;
        return false;
      });
    }

    // Filter by date range
    if (selectedFilters.dateRange.length > 0) {
      filtered = filtered.filter(course => {
        if (!course.completionDate) return false;

        const courseDate = new Date(course.completionDate);
        const now = new Date();
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(now.getFullYear() - 1);

        if (
          selectedFilters.dateRange.includes('Last 3 Months') &&
          courseDate >= threeMonthsAgo
        )
          return true;
        if (
          selectedFilters.dateRange.includes('Last 6 Months') &&
          courseDate >= sixMonthsAgo
        )
          return true;
        if (
          selectedFilters.dateRange.includes('Last Year') &&
          courseDate >= oneYearAgo
        )
          return true;
        if (
          selectedFilters.dateRange.includes('Older') &&
          courseDate < oneYearAgo
        )
          return true;

        return false;
      });
    }

    // Filter by certification
    if (selectedFilters.certification) {
      filtered = filtered.filter(course => course.certificate);
    }

    // Sort courses
    filtered.sort((a, b) => {
      // Handle null completion dates (in-progress courses)
      if (!a.completionDate && !b.completionDate) return 0;
      if (!a.completionDate) return 1;
      if (!b.completionDate) return -1;

      const dateA = new Date(a.completionDate);
      const dateB = new Date(b.completionDate);

      switch (sortBy) {
        case 'date-desc':
          return dateB - dateA;
        case 'date-asc':
          return dateA - dateB;
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        default:
          return dateB - dateA;
      }
    });

    return filtered;
  };

  // Group courses by category
  const getCoursesByCategory = () => {
    const filtered = getFilteredCourses();
    const grouped = {};

    filtered.forEach(course => {
      if (!grouped[course.category]) {
        grouped[course.category] = [];
      }
      grouped[course.category].push(course);
    });

    return grouped;
  };

  // Render list view item
  const renderListItem = ({ item }) => (
    <TouchableOpacity
      style={styles.courseCard}
      onPress={() =>
        navigation.navigate('CourseDetails', { courseId: item.id })
      }
    >
      <View style={styles.courseCardHeader}>
        <View style={styles.courseImageContainer}>
          <Image
            source={{ uri: (item.thumbnail && typeof item.thumbnail==='string' && item.thumbnail.startsWith('http')) ? item.thumbnail : undefined }}
            style={styles.courseImage}
            resizeMode="cover"
          />
          {item.progress < 100 && (
            <View style={styles.progressOverlay}>
              <Text style={styles.progressText}>{item.progress}%</Text>
            </View>
          )}
        </View>

        <View style={styles.courseTitleContainer}>
          <Text style={styles.courseTitle}>{item.title}</Text>
          <Text style={styles.courseProvider}>{item.provider}</Text>

          <View style={styles.courseMetaContainer}>
            <View style={styles.courseMeta}>
              <Ionicons name="calendar-outline" size={14} color="#64748b" />
              <Text style={styles.courseMetaText}>
                {item.completionDate
                  ? new Date(item.completionDate).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : 'In Progress'}
              </Text>
            </View>

            <View style={styles.courseMeta}>
              <Ionicons name="time-outline" size={14} color="#64748b" />
              <Text style={styles.courseMetaText}>{item.duration}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.courseCardContent}>
        <View style={styles.skillsContainer}>
          <Text style={styles.skillsLabel}>Skills:</Text>
          <View style={styles.skillTags}>
            {item.skills.slice(0, 3).map((skill, index) => (
              <View key={index} style={styles.skillTag}>
                <Text style={styles.skillTagText}>{skill}</Text>
              </View>
            ))}
            {item.skills.length > 3 && (
              <View style={styles.skillTag}>
                <Text style={styles.skillTagText}>
                  +{item.skills.length - 3}
                </Text>
              </View>
            )}
          </View>
        </View>

        <View style={styles.courseActions}>
          {item.certificate && (
            <TouchableOpacity
              style={styles.certificateButton}
              onPress={() => shareCertificate(item)}
            >
              <MaterialCommunityIcons
                name="certificate"
                size={16}
                color="#0e4e96"
              />
              <Text style={styles.certificateButtonText}>Certificate</Text>
            </TouchableOpacity>
          )}

          {item.score && (
            <View style={styles.scoreContainer}>
              <Text style={styles.scoreLabel}>Score</Text>
              <View style={styles.scoreCircle}>
                <Text style={styles.scoreText}>{item.score}%</Text>
              </View>
            </View>
          )}
        </View>
      </View>

      {item.progress < 100 && (
        <View style={styles.progressBarContainer}>
          <View style={[styles.progressBar, { width: `${item.progress}%` }]} />
        </View>
      )}
    </TouchableOpacity>
  );

  // Render grid view item
  const renderGridItem = ({ item }) => (
    <TouchableOpacity
      style={styles.gridCard}
      onPress={() =>
        navigation.navigate('CourseDetails', { courseId: item.id })
      }
    >
      <View style={styles.gridImageContainer}>
        <Image
          source={{ uri: (item.thumbnail && typeof item.thumbnail==='string' && item.thumbnail.startsWith('http')) ? item.thumbnail : undefined }}
          style={styles.gridImage}
          resizeMode="cover"
        />
        {item.progress < 100 && (
          <View style={styles.gridProgressOverlay}>
            <Text style={styles.gridProgressText}>{item.progress}%</Text>
          </View>
        )}
        {item.certificate && (
          <View style={styles.certificateBadge}>
            <MaterialCommunityIcons name="certificate" size={16} color="#fff" />
          </View>
        )}
      </View>

      <View style={styles.gridCardContent}>
        <Text style={styles.gridCourseTitle} numberOfLines={2}>
          {item.title}
        </Text>
        <Text style={styles.gridCourseProvider} numberOfLines={1}>
          {item.provider}
        </Text>

        <View style={styles.gridCourseMeta}>
          <Text style={styles.gridCourseDate}>
            {item.completionDate
              ? new Date(item.completionDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : 'In Progress'}
          </Text>

          {item.score && (
            <View style={styles.gridScoreCircle}>
              <Text style={styles.gridScoreText}>{item.score}%</Text>
            </View>
          )}
        </View>
      </View>

      {item.progress < 100 && (
        <View style={styles.gridProgressBarContainer}>
          <View
            style={[styles.gridProgressBar, { width: `${item.progress}%` }]}
          />
        </View>
      )}
    </TouchableOpacity>
  );

  // Render timeline view
  const renderTimelineItem = ({ item }) => (
    <View style={styles.timelineItem}>
      <View style={styles.timelineDot} />
      <View style={styles.timelineContent}>
        <Text style={styles.timelineDate}>
          {item.completionDate
            ? new Date(item.completionDate).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : 'In Progress'}
        </Text>
        <TouchableOpacity
          onPress={() =>
            navigation.navigate('CourseDetails', { courseId: item.id })
          }
        >
          <Text style={styles.timelineTitle}>{item.title}</Text>
        </TouchableOpacity>
        <Text style={styles.timelineProvider}>{item.provider}</Text>
        {item.certificate && (
          <TouchableOpacity
            style={styles.timelineCertificateButton}
            onPress={() => shareCertificate(item)}
          >
            <MaterialCommunityIcons
              name="certificate"
              size={14}
              color="#0e4e96"
            />
            <Text style={styles.timelineCertificateButtonText}>
              View Certificate
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  // Render category view
  const renderCategoryView = () => {
    const coursesByCategory = getCoursesByCategory();
    return (
      <ScrollView contentContainerStyle={styles.categoryScrollViewContent}>
        {Object.keys(coursesByCategory)
          .sort()
          .map(category => (
            <View key={category} style={styles.categorySection}>
              <Text style={styles.categorySectionTitle}>{category}</Text>
              <FlatList
                data={coursesByCategory[category]}
                renderItem={renderListItem} // Using list item rendering for courses within categories
                keyExtractor={item => item.id}
                scrollEnabled={false} // Disable inner scroll
              />
            </View>
          ))}
      </ScrollView>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor="#f0f4f8" />
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Previous Courses</Text>
        <TouchableOpacity onPress={toggleFilter} style={styles.filterButton}>
          <Ionicons name="filter" size={24} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      <Animated.View
        style={[
          styles.filterContainer,
          {
            height: filterHeight.interpolate({
              inputRange: [0, 1],
              outputRange: [0, Dimensions.get('window').height * 0.5], // Max height for filter options
            }),
          },
        ]}
      >
        {filterVisible && (
          <ScrollView contentContainerStyle={styles.filterScrollViewContent}>
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Filter by Category</Text>
              <View style={styles.filterOptionsContainer}>
                {[
                  'Mechanical Engineering',
                  'Electrical Engineering',
                  'Civil Engineering',
                  'Software Engineering',
                  'Aerospace Engineering',
                  'Energy Engineering',
                  'Chemical Engineering',
                  'Robotics',
                ].map(cat => (
                  <TouchableOpacity
                    key={cat}
                    style={[
                      styles.filterOption,
                      selectedFilters.category.includes(cat) &&
                        styles.filterOptionSelected,
                    ]}
                    onPress={() => handleFilterSelect('category', cat)}
                  >
                    <Text
                      style={[
                        styles.filterOptionText,
                        selectedFilters.category.includes(cat) &&
                          styles.filterOptionTextSelected,
                      ]}
                    >
                      {cat}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>
                Filter by Completion Status
              </Text>
              <View style={styles.filterOptionsContainer}>
                {['Completed', 'In Progress'].map(status => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.filterOption,
                      selectedFilters.completion.includes(status) &&
                        styles.filterOptionSelected,
                    ]}
                    onPress={() => handleFilterSelect('completion', status)}
                  >
                    <Text
                      style={[
                        styles.filterOptionText,
                        selectedFilters.completion.includes(status) &&
                          styles.filterOptionTextSelected,
                      ]}
                    >
                      {status}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>
                Filter by Date Range
              </Text>
              <View style={styles.filterOptionsContainer}>
                {['Last 3 Months', 'Last 6 Months', 'Last Year', 'Older'].map(
                  range => (
                    <TouchableOpacity
                      key={range}
                      style={[
                        styles.filterOption,
                        selectedFilters.dateRange.includes(range) &&
                          styles.filterOptionSelected,
                      ]}
                      onPress={() => handleFilterSelect('dateRange', range)}
                    >
                      <Text
                        style={[
                          styles.filterOptionText,
                          selectedFilters.dateRange.includes(range) &&
                            styles.filterOptionTextSelected,
                        ]}
                      >
                        {range}
                      </Text>
                    </TouchableOpacity>
                  )
                )}
              </View>
            </View>

            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Certification</Text>
              <View style={styles.switchContainer}>
                <Text style={styles.switchLabel}>
                  Show only certified courses
                </Text>
                <Switch
                  value={selectedFilters.certification}
                  onValueChange={() =>
                    handleFilterSelect('certification', null)
                  }
                  trackColor={{ false: '#767577', true: '#81b0ff' }}
                  thumbColor={
                    selectedFilters.certification ? '#0e4e96' : '#f4f3f4'
                  }
                />
              </View>
            </View>

            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Sort By</Text>
              <View style={styles.filterOptionsContainer}>
                {[
                  {
                    label: 'Date (Newest First)',
                    value: 'date-desc',
                  },
                  {
                    label: 'Date (Oldest First)',
                    value: 'date-asc',
                  },
                  {
                    label: 'Title (A-Z)',
                    value: 'title-asc',
                  },
                  {
                    label: 'Title (Z-A)',
                    value: 'title-desc',
                  },
                ].map(sortOpt => (
                  <TouchableOpacity
                    key={sortOpt.value}
                    style={[
                      styles.filterOption,
                      sortBy === sortOpt.value && styles.filterOptionSelected,
                    ]}
                    onPress={() => setSortBy(sortOpt.value)}
                  >
                    <Text
                      style={[
                        styles.filterOptionText,
                        sortBy === sortOpt.value &&
                          styles.filterOptionTextSelected,
                      ]}
                    >
                      {sortOpt.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <TouchableOpacity
              onPress={clearFilters}
              style={styles.clearFiltersButton}
            >
              <Text style={styles.clearFiltersButtonText}>
                Clear All Filters
              </Text>
            </TouchableOpacity>
          </ScrollView>
        )}
      </Animated.View>

      <View style={styles.searchAndDisplayOptions}>
        <View style={styles.searchBarContainer}>
          <Ionicons
            name="search"
            size={20}
            color="#64748B"
            style={styles.searchBarIcon}
          />
          <TextInput
            style={styles.searchBarInput}
            placeholder="Search courses..."
            placeholderTextColor="#999"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity
              onPress={() => setSearchQuery('')}
              style={styles.clearSearchButton}
            >
              <Ionicons name="close-circle" size={20} color="#64748B" />
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.displayOptions}>
          <TouchableOpacity
            style={[
              styles.displayOptionButton,
              activeView === 'list' && styles.displayOptionButtonActive,
            ]}
            onPress={() => setActiveView('list')}
          >
            <Ionicons
              name="list"
              size={24}
              color={activeView === 'list' ? '#fff' : '#0e4e96'}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.displayOptionButton,
              activeView === 'grid' && styles.displayOptionButtonActive,
            ]}
            onPress={() => setActiveView('grid')}
          >
            <Ionicons
              name="grid"
              size={24}
              color={activeView === 'grid' ? '#fff' : '#0e4e96'}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.displayOptionButton,
              activeView === 'timeline' && styles.displayOptionButtonActive,
            ]}
            onPress={() => setActiveView('timeline')}
          >
            <Ionicons
              name="hourglass"
              size={24}
              color={activeView === 'timeline' ? '#fff' : '#0e4e96'}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.displayOptionButton,
              activeView === 'category' && styles.displayOptionButtonActive,
            ]}
            onPress={() => setActiveView('category')}
          >
            <MaterialCommunityIcons
              name="shape"
              size={24}
              color={activeView === 'category' ? '#fff' : '#0e4e96'}
            />
          </TouchableOpacity>
        </View>
      </View>

      {getFilteredCourses().length === 0 ? (
        <View style={styles.noResultsContainer}>
          <Text style={styles.noResultsText}>
            No courses found matching your criteria.
          </Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.scrollViewContent}>
          {activeView === 'list' && (
            <FlatList
              data={getFilteredCourses()}
              renderItem={renderListItem}
              keyExtractor={item => item.id}
              contentContainerStyle={styles.flatListContent}
              scrollEnabled={false} // Disable FlatList's own scroll as it's inside a ScrollView
            />
          )}
          {activeView === 'grid' && (
            <FlatList
              data={getFilteredCourses()}
              renderItem={renderGridItem}
              keyExtractor={item => item.id}
              numColumns={2}
              columnWrapperStyle={styles.gridColumnWrapper}
              contentContainerStyle={styles.flatListContent}
              scrollEnabled={false}
            />
          )}
          {activeView === 'timeline' && (
            <FlatList
              data={getFilteredCourses()}
              renderItem={renderTimelineItem}
              keyExtractor={item => item.id}
              contentContainerStyle={styles.flatListContent}
              scrollEnabled={false}
            />
          )}
          {activeView === 'category' && renderCategoryView()}
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  filterButton: {
    padding: 5,
  },
  filterContainer: {
    backgroundColor: '#fff',
    overflow: 'hidden',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  filterScrollViewContent: {
    padding: 15,
  },
  filterSection: {
    marginBottom: 20,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  filterOptionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  filterOption: {
    backgroundColor: '#e9eef4',
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 15,
    marginRight: 10,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#e9eef4',
  },
  filterOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  filterOptionText: {
    color: '#0e4e96',
    fontSize: 14,
  },
  filterOptionTextSelected: {
    color: '#fff',
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#e9eef4',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  switchLabel: {
    fontSize: 14,
    color: '#333',
  },
  clearFiltersButton: {
    backgroundColor: '#ff6b6b',
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 10,
  },
  clearFiltersButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  searchAndDisplayOptions: {
    padding: 15,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  searchBarContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f4f8',
    borderRadius: 10,
    paddingHorizontal: 10,
    marginRight: 10,
    height: 40,
  },
  searchBarIcon: {
    marginRight: 8,
  },
  searchBarInput: {
    flex: 1,
    height: '100%',
    color: '#333',
  },
  clearSearchButton: {
    padding: 5,
  },
  displayOptions: {
    flexDirection: 'row',
    backgroundColor: '#f0f4f8',
    borderRadius: 10,
    overflow: 'hidden',
  },
  displayOptionButton: {
    padding: 8,
    borderRightWidth: 1,
    borderRightColor: '#e0e0e0',
  },
  displayOptionButtonActive: {
    backgroundColor: '#0e4e96',
  },
  scrollViewContent: {
    padding: 15,
    paddingBottom: 20,
  },
  flatListContent: {
    // No specific padding here, handled by scrollViewContent
  },
  courseCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    marginBottom: 15,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  courseCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
  },
  courseImageContainer: {
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: 'hidden',
    marginRight: 10,
    position: 'relative',
  },
  courseImage: {
    width: '100%',
    height: '100%',
  },
  progressOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  courseTitleContainer: {
    flex: 1,
  },
  courseTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  courseProvider: {
    fontSize: 13,
    color: '#666',
    marginBottom: 5,
  },
  courseMetaContainer: {
    flexDirection: 'row',
    marginTop: 5,
  },
  courseMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 15,
  },
  courseMetaText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 5,
  },
  courseCardContent: {
    paddingHorizontal: 10,
    paddingBottom: 10,
  },
  skillsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  skillsLabel: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#333',
    marginRight: 5,
  },
  skillTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  skillTag: {
    backgroundColor: '#e9eef4',
    borderRadius: 5,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginRight: 5,
    marginBottom: 5,
  },
  skillTagText: {
    fontSize: 12,
    color: '#0e4e96',
  },
  courseActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
  },
  certificateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e9eef4',
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  certificateButtonText: {
    color: '#0e4e96',
    fontSize: 13,
    fontWeight: 'bold',
    marginLeft: 5,
  },
  scoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  scoreLabel: {
    fontSize: 13,
    color: '#666',
    marginRight: 5,
  },
  scoreCircle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
  },
  scoreText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  progressBarContainer: {
    height: 5,
    backgroundColor: '#e0e0e0',
    borderRadius: 2.5,
    marginHorizontal: 10,
    marginBottom: 10,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#0e4e96',
    borderRadius: 2.5,
  },
  gridCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    marginBottom: 15,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
    width: '48%', // Adjust for 2 columns with some margin
  },
  gridColumnWrapper: {
    justifyContent: 'space-between',
  },
  gridImageContainer: {
    width: '100%',
    height: 120,
    overflow: 'hidden',
    position: 'relative',
  },
  gridImage: {
    width: '100%',
    height: '100%',
  },
  gridProgressOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  gridProgressText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 14,
  },
  certificateBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: '#0e4e96',
    borderRadius: 15,
    padding: 5,
  },
  gridCardContent: {
    padding: 10,
  },
  gridCourseTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  gridCourseProvider: {
    fontSize: 11,
    color: '#666',
    marginBottom: 5,
  },
  gridCourseMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 5,
  },
  gridCourseDate: {
    fontSize: 11,
    color: '#666',
  },
  gridScoreCircle: {
    width: 25,
    height: 25,
    borderRadius: 12.5,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
  },
  gridScoreText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  gridProgressBarContainer: {
    height: 4,
    backgroundColor: '#e0e0e0',
    borderRadius: 2,
    marginHorizontal: 10,
    marginBottom: 10,
    overflow: 'hidden',
  },
  gridProgressBar: {
    height: '100%',
    backgroundColor: '#0e4e96',
    borderRadius: 2,
  },
  timelineItem: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#0e4e96',
    marginTop: 5,
    marginRight: 15,
    borderWidth: 2,
    borderColor: '#e9eef4',
  },
  timelineContent: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  timelineDate: {
    fontSize: 12,
    color: '#666',
    marginBottom: 5,
  },
  timelineTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  timelineProvider: {
    fontSize: 13,
    color: '#666',
    marginBottom: 10,
  },
  timelineCertificateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e9eef4',
    borderRadius: 20,
    paddingVertical: 6,
    paddingHorizontal: 10,
    alignSelf: 'flex-start',
  },
  timelineCertificateButtonText: {
    color: '#0e4e96',
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 5,
  },
  categoryScrollViewContent: {
    paddingBottom: 20,
  },
  categorySection: {
    marginBottom: 20,
  },
  categorySectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 10,
    paddingHorizontal: 15,
  },
  noResultsContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 50,
  },
  noResultsText: {
    fontSize: 16,
    color: '#666',
  },
});
